import { Link } from "react-router-dom";
import { Container } from "@/components/ui/Container";
import { Icon, type IconName } from "@/components/ui/Icon";

interface Sector {
  icon: IconName;
  title: string;
  blurb: string;
  to: string;
}

/**
 * The market sectors Frazzetto cuts for. Sits directly under the hero as a
 * fast "is this for me?" signal for civil, commercial, domestic and
 * industrial clients. Each medallion links through to the services list.
 */
const sectors: Sector[] = [
  { icon: "road", title: "Civil", blurb: "Roads, trenching and civil works", to: "/services" },
  { icon: "building", title: "Commercial", blurb: "Fit-outs, retail and commercial builds", to: "/services" },
  { icon: "home", title: "Domestic", blurb: "Renovations, extensions and home jobs", to: "/services" },
  { icon: "industrial", title: "Industrial", blurb: "Factories, warehouses and plant", to: "/services" },
];

export function SectorsBand() {
  return (
    <section className="sectors" aria-label="Sectors we work for">
      <Container>
        <p className="sectors__eyebrow">Concrete cutting for every sector</p>
        <ul className="sectors__grid">
          {sectors.map((s) => (
            <li key={s.title}>
              <Link to={s.to} className="sector">
                <span className="sector__medallion">
                  <Icon name={s.icon} size={34} />
                </span>
                <span className="sector__title">{s.title}</span>
                <span className="sector__sub">Concrete Cutting</span>
                <span className="sector__blurb">{s.blurb}</span>
              </Link>
            </li>
          ))}
        </ul>
      </Container>

      <style>{`
        .sectors {
          background: var(--white);
          padding-block: clamp(var(--space-6), 5vw, var(--space-8));
          border-bottom: 1px solid var(--border-subtle);
        }
        .sectors__eyebrow {
          text-align: center;
          font-family: var(--font-mono);
          font-weight: 400;
          font-size: 12.5px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--orange-hover);
          margin-bottom: var(--space-6);
        }
        .sectors__grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: var(--space-5);
        }
        .sector {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: var(--space-2);
          padding: var(--space-4) var(--space-3);
          border-radius: var(--radius-md);
          text-decoration: none;
          transition: background var(--duration-base) var(--ease-out), transform var(--duration-base) var(--ease-out);
        }
        .sector__medallion {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 92px;
          height: 92px;
          margin-bottom: var(--space-2);
          border-radius: 50%;
          color: var(--navy);
          background: var(--white);
          border: 2px solid var(--orange);
          box-shadow: inset 0 0 0 6px var(--white), 0 6px 16px rgba(32,31,29,0.10);
          transition: background var(--duration-base) var(--ease-out), color var(--duration-base) var(--ease-out),
            transform var(--duration-base) var(--ease-out), box-shadow var(--duration-base) var(--ease-out);
        }
        .sector__title {
          font-family: var(--font-display);
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: -0.01em;
          font-size: var(--fs-500);
          color: var(--navy);
          line-height: 1;
        }
        .sector__sub {
          font-family: var(--font-display);
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          font-size: var(--fs-100);
          color: var(--orange-hover);
        }
        .sector__blurb {
          font-size: var(--fs-200);
          color: var(--muted-text);
          max-width: 22ch;
          margin-top: 2px;
        }
        /* Only apply hover styling on real pointer devices — on touch screens
           :hover sticks after a tap and leaves one medallion looking different
           from the rest. Keyboard focus keeps its own styling below. */
        @media (hover: hover) {
          .sector:hover {
            background: var(--concrete-grey);
            transform: translateY(-3px);
          }
          .sector:hover .sector__medallion {
            background: var(--orange);
            color: var(--white);
            box-shadow: inset 0 0 0 6px var(--white), 0 10px 22px rgba(232,83,28,0.32);
          }
        }
        .sector:focus-visible {
          background: var(--concrete-grey);
          transform: translateY(-3px);
        }
        .sector:focus-visible .sector__medallion {
          background: var(--orange);
          color: var(--white);
          box-shadow: inset 0 0 0 6px var(--white), 0 10px 22px rgba(232,83,28,0.32);
        }

        @media (max-width: 760px) {
          .sectors__grid { grid-template-columns: 1fr 1fr; gap: var(--space-6) var(--space-4); }
          .sector { padding: var(--space-3) var(--space-2); }
          .sector__medallion { width: 76px; height: 76px; }
        }
      `}</style>
    </section>
  );
}
